import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge, Spinner, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faUsers, faPlus } from '@fortawesome/free-solid-svg-icons';
import SchoolUserService from '../../services/schoolUser.service';
import MunicipalityUserService from '../../services/municipalityUser.service';
import ManagerService from '../../services/manager.service';
import { useToast } from '../../contexts/ToastContext';

const MunicipalStaff = () => {
  const { showToast } = useToast();
  const [staff, setStaff] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    role: 'PRINCIPAL'
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar escolas - dados já vêm filtrados do backend por município
      const schoolsData = await SchoolUserService.getAll();
      setSchools(schoolsData.results || []);

      // Buscar diretores e coordenadores - dados já vêm filtrados do backend
      const staffData = await SchoolUserService.getAll();

      // Filtrar apenas gestores (PRINCIPAL e COORD) - filtro de role no frontend
      const municipalityStaff = staffData.results.filter(
        su => ['PRINCIPAL', 'COORD'].includes(su.role)
      );

      setStaff(municipalityStaff);

    } catch (err) {
      setError('Erro ao carregar dados. Tente novamente.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      user: staffMember.user,
      school: staffMember.school,
      role: staffMember.role,
      active: staffMember.active
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedStaff(null);
    setFormData({
      user: '',
      school: '',
      role: '',
      active: true
    });
  };

  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      school: '',
      role: 'PRINCIPAL'
    });
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      user: '',
      school: '',
      role: '',
      active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        active: formData.active
      };
      await MunicipalityUserService.partialUpdate(selectedStaff.id, payload);
      await fetchData();
      handleCloseModal();
      showToast('Gestor atualizado com sucesso!', 'success');
    } catch (err) {
      console.error('Error updating staff:', err);
      showToast('Erro ao atualizar. Tente novamente.', 'danger');
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        school: formData.school,
        role: formData.role
      };

      const response = await ManagerService.createManager(payload);

      showToast(response.message || 'Gestor criado com sucesso! Um email foi enviado para definição de senha.', 'success');

      await fetchData();
      handleCloseCreateModal();
    } catch (err) {
      const errorMsg = err.response?.data?.school?.[0] ||
                       err.response?.data?.email?.[0] ||
                       err.response?.data?.message ||
                       'Erro ao criar gestor. Tente novamente.';
      showToast(errorMsg, 'danger');
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Gestão de Diretores e Coordenadores</h4>
          <p className="mb-0">Gerencie diretores e coordenadores das escolas municipais</p>
        </div>
      </div>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Lista de Gestores
                  </h5>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenCreateModal}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Novo Vínculo
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && staff.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">Nenhum gestor encontrado.</p>
                </div>
              )}
              {!loading && !error && staff.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Nome</th>
                      <th>Escola</th>
                      <th>Função</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((staffMember) => (
                      <tr key={staffMember.id}>
                        <td className="fw-bold">{staffMember.user_name}</td>
                        <td>{staffMember.school_name}</td>
                        <td>
                          <Badge bg={staffMember.role === 'PRINCIPAL' ? 'primary' : 'info'}>
                            {staffMember.role_display}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={staffMember.active ? 'success' : 'secondary'}>
                            {staffMember.active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleEdit(staffMember)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Edição */}
      <Modal show={showEditModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Criação */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Novo Gestor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome Completo</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nome completo do gestor"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@exemplo.com"
                required
              />
              <Form.Text className="text-muted">
                Um email será enviado para este endereço com instruções para definir a senha.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Telefone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(00) 00000-0000"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Escola</Form.Label>
              <Form.Select
                name="school"
                value={formData.school}
                onChange={handleInputChange}
                required
              >
                <option value="">Selecione uma escola</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Função</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
              >
                <option value="PRINCIPAL">Diretor</option>
                <option value="COORD">Coordenador</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!formData.name || !formData.email || !formData.school || !formData.role}
          >
            Criar Gestor
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MunicipalStaff;
